/* eslint-disable react/display-name */

import React from 'react';
import Promise from 'bluebird';
import _ from 'lodash';
import AddressWrapper from 'shared/components/address/wrapper';
import withGeocoder from 'shared/components/address/geocoder';
import Select from 'scy/Select';
import FormField from 'scy/FormField';
import { Checkbox } from 'scy/Input';
import { Warning } from 'scy/Warning';
import PhoneInput from 'shared/components/phoneInput';
import Toggle from 'scy/Toggle';
import { trackNow } from 'shared/models/analytic';
import HomeIcon from 'images/icons/residential.svg?react-svg';
import WorkIcon from 'images/icons/business.svg?react-svg';
import { width100, textCenter, textRight } from 'scy/utils.mod.scss';
import { LoadingButton, CloseButton } from 'scy/buttons';
import { Paragraph, SecondarySmallAlt } from 'scy/text';
import {
  checkbox,
  checkboxLabel
} from 'components/checkoutPage/form/styles.mod.scss';
import margin from 'scy/margin.mod.scss';

let UserAddressForm = class UserAddressForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      address: props.address.address,
      zip: props.address.zip,
      city: props.address.city || '',
      state: props.address.state || '',
      type: props.address.type || 'Residential',
      label: props.address.label || 'Home',
      saved: !!props.address.saved,
      errors: {},
      error: null,
      loading: false
    };

    /* Maintain separate data model b/c passing large state to FormFields causes perf issues */
    this.model = {
      ...props.address,
      type: this.state.type,
      label: this.state.label
    };

    this.selectAutocompleteAddress = this.selectAutocompleteAddress.bind(this);
    this.setAddressType = this.setAddressType.bind(this);
    this.setAddressLabel = this.setAddressLabel.bind(this);
    this.validateAddress = this.validateAddress.bind(this);
    this.validateZip = this.validateZip.bind(this);
    this.toggleSaveAddress = this.toggleSaveAddress.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidMount() {
    if (this.props.autoFocus) {
      if (!this.model.address && this.addressInput) {
        this.addressInput.focus();
      } else if (!this.model.notes && this.notesInput) {
        this.notesInput.focus();
      }
    }
  }

  selectAutocompleteAddress(address) {
    if (!address) return;
    this.setGeocodedFields(address);
    setTimeout(() => this.unitInput.focus(), 250);
  }

  setGeocodedFields(address) {
    this.model = {
      ...this.model,
      lat: address.lat,
      lng: address.lng,
      address: address.address,
      zip: address.zip,
      city: address.city,
      state: address.state
    };

    this.setState({
      address: address.address,
      zip: address.zip,
      city: address.city,
      state: address.state
    });
  }

  setAddressType(type) {
    this.model = { ...this.model, type };
    this.setState({ type });
    if (!this.state.saved) {
      this.setAddressLabel(type === 'Business' ? 'Work' : 'Home');
    }
    trackNow('Address Type Selected', { type });
  }

  setAddressLabel(label) {
    this.model = { ...this.model, label };
    this.setState({ label });
  }

  validateAddress(address) {
    if (_.isEmpty(address)) return "Street Address can't be blank";
  }

  validateZip(zip) {
    if (_.isEmpty(zip)) return "Zip Code can't be blank";
  }

  toggleSaveAddress() {
    this.setState(prevState => ({ saved: !prevState.saved }));
    this.model = {
      ...this.model,
      saved: !this.model.saved,
      recent: !!this.model.saved
    };
    this.model.saved
      ? trackNow('Save Address Selected')
      : trackNow('Save Address Deselected');
  }

  userAlteredAddress() {
    return this.state.address !== this.model.address;
  }

  userAlteredZip() {
    return this.state.zip !== this.model.zip;
  }

  checkAddress() {
    this.setState({ error: null });

    const fields = this.model;

    if (!this.userAlteredAddress() && !this.userAlteredZip())
      return Promise.resolve(fields);

    const query = [fields.address, fields.zip].join(' ').trim();
    return this.props
      .geocode(query)
      .catch(() => {
        const error =
          'Your address could not be verified. Please check your street address and zip code, then try again.';
        return Promise.reject(new Error(error));
      })
      .then(address => {
        if (address.zip !== fields.zip) {
          const error = `The street address does not match the zip code. Do you mean ${
            address.zip
          }?`;
          return Promise.reject(new Error(error));
        }
        this.setGeocodedFields(address);
        return { ...fields, ...address };
      });
  }

  trimFields(address) {
    if (address.type !== 'Business') {
      if (address.businessName) delete address.businessName;
      if (address.businessPhone) delete address.businessPhone;
    }
    if (!address.saved && address.label) delete address.label;
    return address;
  }

  validateFields() {
    const { address, zip, type, businessPhone } = this.model;

    let errors = {};
    errors.address = this.validateAddress(address) || null;
    errors.zip = this.validateZip(zip) || null;
    errors.businessPhone =
      type === 'Business' && this.validateBusinessPhone
        ? this.validateBusinessPhone(businessPhone)
        : null;

    this.setState({ errors });

    if (_.some(errors)) return Promise.reject(new Error());
    return Promise.resolve();
  }

  handleSubmit(e) {
    e.preventDefault();
    this.setState({ loading: true, error: null });
    return this.props
      .onBeforeSubmit()
      .then(() => this.validateFields())
      .then(() => this.checkAddress())
      .then(address => this.trimFields(address))
      .then(address => this.props.onSubmit(address))
      .then(() => this.props.onSuccess && this.props.onSuccess(this.model))
      .catch(({ message }) => {
        this.setState({ loading: false, error: message });
      });
  }

  render() {
    const FieldsContainer = this.props.fieldsContainer;

    return (
      <form onSubmit={this.handleSubmit}>
        <FieldsContainer>
          {this.state.error && <Warning>{this.state.error}</Warning>}
          {this.props.onCancel && <CloseButton onClick={this.props.onCancel} />}
          <div className="row">
            <div className="col-xs-12 col-md-8">
              <AddressWrapper
                inputId="delivery-street-address"
                setAddress={this.selectAutocompleteAddress}
              >
                <FormField
                  id="delivery-street-address"
                  name="address"
                  labelText="Street Address"
                  initialValue={this.model.address}
                  updatedValue={this.state.address}
                  onChange={address =>
                    (this.model = { ...this.model, address })
                  }
                  validate={this.validateAddress}
                  error={this.state.errors.address}
                  inputRef={input => (this.addressInput = input)}
                />
              </AddressWrapper>
            </div>

            <div className="col-xs-12 col-md-4">
              <FormField
                id="delivery-zip-code"
                name="zip"
                labelText={
                  this.state.city && !this.userAlteredZip()
                    ? `${this.state.city}, ${this.state.state}`
                    : 'Zip Code'
                }
                initialValue={this.model.zip}
                updatedValue={this.state.zip}
                onChange={zip => (this.model = { ...this.model, zip })}
                validate={this.validateZip}
                error={this.state.errors.zip}
              />
            </div>
          </div>

          <FormField
            id="delivery-unit-number"
            labelText="Apt, suite, unit (optional)"
            initialValue={this.model.unit}
            onChange={unit => (this.model = { ...this.model, unit })}
            inputRef={input => (this.unitInput = input)}
          />

          {!this.model.shipping && (
            <FormField
              textarea
              id="delivery-notes"
              labelText="Delivery notes (e.g. door code) (optional)"
              initialValue={this.model.notes}
              onChange={notes => (this.model = { ...this.model, notes })}
              inputRef={input => (this.notesInput = input)}
            />
          )}

          <Paragraph className={`${textCenter} ${margin.bottom_sm}`}>
            Someone 21 years or older must be present upon delivery with a valid
            government issued ID.
          </Paragraph>

          <Toggle
            name="addressType"
            className={margin.bottom_sm}
            options={[
              {
                value: 'Residential',
                label: 'Residential',
                icon: <HomeIcon />
              },
              { value: 'Business', label: 'Business', icon: <WorkIcon /> }
            ]}
            value={this.state.type}
            onChange={this.setAddressType}
          />

          {this.state.type === 'Business' && (
            <div className="row">
              <div className="col-xs-12 col-md-6">
                <FormField
                  id="delivery-business-name"
                  labelText="Business Name (optional)"
                  initialValue={this.model.businessName}
                  onChange={businessName =>
                    (this.model = { ...this.model, businessName })
                  }
                />
              </div>

              <div className="col-xs-12 col-md-6">
                <PhoneInput
                  id="delivery-business-phone"
                  labelText="Business Phone (optional)"
                  initialValue={this.model.businessPhone}
                  onChange={businessPhone =>
                    (this.model = { ...this.model, businessPhone })
                  }
                  notValidMessage="Enter a valid phone number"
                  forwardValidator={validate =>
                    (this.validateBusinessPhone = validate)
                  }
                  error={this.state.errors.businessPhone}
                />
              </div>
            </div>
          )}

          <div className={textRight}>
            <SecondarySmallAlt
              tag="label"
              htmlFor="saveAddress"
              className={checkboxLabel}
            >
              <span className={margin.right_sm}>
                Save this address for future orders
              </span>
              <Checkbox
                name="saveAddress"
                id="saveAddress"
                className={checkbox}
                checked={this.state.saved}
                onChange={this.toggleSaveAddress}
              />
            </SecondarySmallAlt>
          </div>

          {this.state.saved && (
            <div className="row">
              <div className="col-xs-12 col-md-4 col-md-offset-8">
                <Select
                  instanceId="address-type"
                  name="address-name"
                  className={margin.top_sm}
                  value={this.state.label}
                  options={[
                    { value: 'Home', label: 'Home' },
                    { value: 'Work', label: 'Work' },
                    { value: 'Other', label: 'Other' }
                  ]}
                  onChange={({ value }) => this.setAddressLabel(value)}
                  clearable={false}
                  searchable={false}
                />
              </div>
            </div>
          )}
        </FieldsContainer>

        <div className="row">
          <div className="col-xs-12 col-md-5 col-md-offset-7 col-lg-4 col-lg-offset-8">
            <LoadingButton
              type="submit"
              className={`${width100} ${margin.top_sm}`}
              disabled={this.state.loading}
              loading={this.state.loading}
            >
              Continue
            </LoadingButton>
          </div>
        </div>
      </form>
    );
  }
};

UserAddressForm.defaultProps = {
  fieldsContainer: props => <div>{props.children}</div>
};

UserAddressForm = withGeocoder(UserAddressForm);

export default UserAddressForm;
